/**
 * Centralized configuration for inventory measurement units
 * This file defines all supported units for weight and volume measurements
 */
export const WEIGHT_UNITS = {
    kg: { label: 'kg', name: 'Kilogram', conversionToKg: 1 },
    g: { label: 'g', name: 'Gram', conversionToKg: 0.001 },
    lb: { label: 'lb', name: 'Pound', conversionToKg: 0.453592 },
    oz: { label: 'oz', name: 'Ounce', conversionToKg: 0.0283495 },
};
export const VOLUME_UNITS = {
    l: { label: 'L', name: 'Liter', conversionToL: 1 },
    ml: { label: 'mL', name: 'Milliliter', conversionToL: 0.001 },
    'm³': { label: 'm³', name: 'Cubic Meter', conversionToL: 1000 },
    'cm³': { label: 'cm³', name: 'Cubic Centimeter', conversionToL: 0.001 },
};
export const DEFAULT_WEIGHT_UNIT = 'kg';
export const DEFAULT_VOLUME_UNIT = 'l';
/**
 * Get all weight unit keys as an array
 */
export function getWeightUnitKeys() {
    return Object.keys(WEIGHT_UNITS);
}
/**
 * Get all volume unit keys as an array
 */
export function getVolumeUnitKeys() {
    return Object.keys(VOLUME_UNITS);
}
/**
 * Get weight unit label
 */
export function getWeightUnitLabel(unit) {
    return WEIGHT_UNITS[unit]?.label || unit;
}
/**
 * Get volume unit label
 */
export function getVolumeUnitLabel(unit) {
    return VOLUME_UNITS[unit]?.label || unit;
}
/**
 * Convert weight from one unit to another
 */
export function convertWeight(value, fromUnit, toUnit) {
    if (fromUnit === toUnit)
        return value;
    // Convert to kg first, then to target unit
    const valueInKg = value * WEIGHT_UNITS[fromUnit].conversionToKg;
    return valueInKg / WEIGHT_UNITS[toUnit].conversionToKg;
}
/**
 * Convert volume from one unit to another
 */
export function convertVolume(value, fromUnit, toUnit) {
    if (fromUnit === toUnit)
        return value;
    // Convert to liters first, then to target unit
    const valueInL = value * VOLUME_UNITS[fromUnit].conversionToL;
    return valueInL / VOLUME_UNITS[toUnit].conversionToL;
}
/**
 * Validate if a string is a valid weight unit
 */
export function isValidWeightUnit(unit) {
    return unit in WEIGHT_UNITS;
}
/**
 * Validate if a string is a valid volume unit
 */
export function isValidVolumeUnit(unit) {
    return unit in VOLUME_UNITS;
}
//# sourceMappingURL=data:application/json;base64,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